import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Group, Modal, Text } from "@mantine/core";

import { StyledEditModalContent } from "@pages/Patients/Laboratory/components/EditModal/EditModal.styled";

import { handleOnChange } from "@utils/handleOnChange";

import { useUpdateMedication } from "@hooks/mutations/prescription/useUpdateMedication";

import { Medication } from "@customTypes/prescription";


interface EditModalProps {
  medication: Partial<Medication>;
  cId: string;
  opened: boolean;
  close: () => void;
}

export const EditModal = ({
  medication: initialMedication,
  cId,
  opened,
  close,
}: EditModalProps) => {
  const { t } = useTranslation();

  const [medication, setMedication] =
    useState<Partial<Medication>>(initialMedication);
  const [formData, setFormData] = useState<Partial<Medication>>(medication);

  const { mutate: updateMedication, isLoading: isUpdateMedicationLoading } =
    useUpdateMedication();

  const handleUpdateMedication = () => {
    console.log("Update medication form:", formData);
    updateMedication(formData, { onSuccess: close });
  };

  useEffect(() => {
    if (initialMedication) {
      setMedication(initialMedication);
      // console.log(initialMedication);
      setFormData({
        ...initialMedication,
      });
    }
  }, [initialMedication]);

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      title={
        <Group gap={7}>
          <Text fw={500} c="black">
            {t("edit-medication-for")}
          </Text>
          <Badge bg="accent.0" c="accent.6" size="lg">
            {cId ?? ""}
          </Badge>
        </Group>
      }>
      <StyledEditModalContent>
        <form className="form" style={{ marginTop: 0 }}>
          <div className="form-field">
            <label htmlFor="medication">
              {t("medication")} <sup>*</sup>
            </label>
            <input
              type="text"
              placeholder={t("eg", { example: "Paracetamol" })}
              id="medication"
              name="name"
              value={formData.name ?? ""}
              onChange={e => handleOnChange(e, setFormData, formData)}
              required
            />
          </div>

          <div className="form-field">
            <label htmlFor="frequency">{t("frequency")}</label>
            <input
              type="text"
              value={formData.frequency ?? ""}
              name="frequency"
              onChange={e => handleOnChange(e, setFormData, formData)}
            />
          </div>

          <div className="form-group">
            <div className="form-field">
              <label htmlFor="dosage">
                {t("dosage")}
                <sup>*</sup>
              </label>
              <input
                type="text"
                placeholder={t("eg", { example: "50 ml" })}
                id="dosage"
                name="dosage"
                value={formData.dosage ?? ""}
                onChange={e => handleOnChange(e, setFormData, formData)}
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="duration">
                {t("duration")}
                <sup>*</sup>
              </label>
              <input
                type="text"
                placeholder={t("eg", { example: t("2-weeks") })}
                id="duration"
                name="duration"
                value={formData.duration ?? ""}
                onChange={e => handleOnChange(e, setFormData, formData)}
                required
              />
            </div>
          </div>

          <div className="form-field">
            <label htmlFor="reason">{t("reason")}</label>
            <textarea
              rows={5}
              placeholder={t("type-reason")}
              id="reason"
              name="reason"
              value={formData.reason ?? ""}
              onChange={e =>
                handleOnChange(e, setFormData, formData)
              }></textarea>
          </div>
        </form>

        <Group justify="end" mt="xl">
          <PermissionGate permission="medications.edit"><Button
            disabled={
              !formData?.name ||
              !formData?.frequency ||
              !formData.dosage ||
              !formData.duration
            }
            onClick={handleUpdateMedication}
            loading={isUpdateMedicationLoading}>
            {t("update-medication")}
          </Button></PermissionGate>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
